<template>
    <div class="content">
        <v-card>
            <v-card-title primary-title> New Fermenter Run </v-card-title>
            <v-card-text>
                <v-row>
                    <v-col cols="4">
                        <v-text-field label="Name (*)" v-model="experimentName"></v-text-field>
                    </v-col>
                    <v-col cols="4">
                        <ProjectFolderSelect v-model="selectedProjectFolder" />
                    </v-col>
                    <v-col md="4">
                        <v-date-input v-model="startDate" label="Day 0 (*)"></v-date-input>
                    </v-col>
                </v-row>
                <v-row>
                    <v-col cols="4">
                        <ProtocolSelect
                            v-model="selectedProtocol"
                            :protocols="fermenterRunProtocols"
                            :project-folder-option="selectedProjectFolder"
                        />
                    </v-col>
                    <v-col cols="12" sm="4">
                        <v-autocomplete
                            v-model="selectedFermenter"
                            :items="fermenters"
                            label="Fermenter"
                            return-object
                            item-title="displayName"
                            item-value="id"
                        >
                        </v-autocomplete>
                    </v-col>
                    <v-col md="4">
                        Select plate packing direction:
                        <v-radio-group v-model="plateLayout" inline>
                            <v-radio label="Horizontal" value="horizontal"></v-radio>
                            <v-radio label="Vertical" value="vertical"></v-radio>
                        </v-radio-group>
                    </v-col>
                </v-row>
                <v-row>
                    <v-col cols="4">
                        <v-autocomplete
                            :items="experimentGroups"
                            label="Experiment Group (*)"
                            clearable
                            v-model="selectedExperimentGroupId"
                            item-title="name"
                            item-value="id"
                            :rules="requiredSimpleValueRules('Experiment group')"
                        />
                    </v-col>
                </v-row>
                <v-row>
                    <v-col cols="12">
                        <experiment-metadata-template-fields class="ml-n4"
                            metadata-context="fermenterRun"
                            v-model="experimentMetadata"
                        />
                    </v-col>
                </v-row>
                <v-row>
                    <v-col cols="4">
                        <v-checkbox
                            color="primary"
                            label="Create batches using ordered peptides"
                            v-model="useOrderedPeptides"
                            :disabled="true"
                        ></v-checkbox>
                    </v-col>
                    <v-col cols="4">
                        <v-autocomplete
                            v-model="peptideOrders"
                            :items="openOrders"
                            label="Peptide Orders"
                            :item-title="(order) => `${order.orderCode}: ${order.displayName}`"
                            item-value="id"
                            multiple
                            chips
                            deletable-chips
                            return-object
                            clearable
                        ></v-autocomplete>
                    </v-col>
                    <v-col cols="4" v-if="!createBatches">
                        <v-text-field
                            v-model="numberOfBatchesPerPeptide"
                            type="number"
                            label="Number of fermenter and MDA batches per peptide"
                        ></v-text-field>
                    </v-col>
                </v-row>
                <v-row>
                    <v-col cols="3" v-if="createBatches">
                        <v-text-field
                            v-model="numberOfPeptideBatchesPerPeptide"
                            type="number"
                            label="Number of clones per peptide"
                        ></v-text-field>
                    </v-col>
                    <v-col cols="3" v-if="createBatches">
                        <v-text-field
                            v-model="numberOfFermenterBatchesPerPeptideBatch"
                            type="number"
                            label="Number of fermenter and MDA batches per peptide batch"
                        ></v-text-field>
                    </v-col>
                    <v-col cols="3" v-if="createBatches">
                        <v-checkbox
                            v-model="groupByFermenterSeed"
                            label="Group fermenter batches by fermenter seed"
                        ></v-checkbox>
                    </v-col>
                </v-row>
                <v-row>
                    <v-col cols="2">
                        <v-switch v-model="createBatches" label="Create batches"></v-switch>
                    </v-col>
                </v-row>
                <v-row>
                    <v-col cols="12">
                        <entity-bulk-search
                            v-if="createBatches"
                            entities-label="Selected Peptides"
                            :entity-lookup-fn="validatePeptideCodes"
                            @entities-found="handlePeptideCodesValidated"
                        ></entity-bulk-search>
                        <entity-bulk-search
                            v-else
                            entities-label="Selected Peptide Batches"
                            :entity-lookup-fn="validatePeptideBatchCodes"
                            @entities-found="handlePeptideBatchCodesValidated"
                        ></entity-bulk-search>
                    </v-col>
                </v-row>
                <v-row>
                    <v-col cols="4">
                        <v-file-input
                            v-model="file"
                            accept="application/vnd.ms-excel, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                            label="Upload Fermenter Setup Excel File"
                        >
                        </v-file-input>
                        <v-btn
                            v-if="createBatches"
                            color="primary"
                            @click="downloadV2ExcelFiles"
                            :disabled="!isTemplateDownloadAvailable"
                        >
                            Download Template Excel Setup File
                        </v-btn>
                        <v-btn
                            v-else
                            color="primary"
                            @click="downloadLegacyTemplate"
                            :disabled="!isTemplateDownloadAvailable"
                        >
                            Download Template Excel Setup File
                        </v-btn>
                    </v-col>
                </v-row>
                <v-row>
                    <v-col cols="12">
                        <v-card-actions>
                            <v-spacer></v-spacer>
                            <v-btn
                                color="primary"
                                :disabled="!isProcessFermenterRunSetupFileAvailable"
                                @click="upload"
                            >
                                Process Fermenter Run Setup File
                            </v-btn>
                        </v-card-actions>
                    </v-col>
                </v-row>
            </v-card-text>
        </v-card>
    </div>
</template>
<script setup lang="ts">
import type { Ref } from "vue";
import { computed, ref, watch } from "vue";

import ProtocolSelect from "@/components/Experiments/shared/ProtocolSelect.vue";
import ProjectFolderSelect from "@/components/Experiments/shared/ProjectFolderSelect.vue";
import EntityBulkSearch from "@/components/common/EntityBulkSearch.vue";
import { withLoadingScreen } from "@/lib/loading";
import {
    BenchlingProjectFolderOption,
    BenchlingProtocol,
    fetchFermenterRunProtocols,
} from "@/services/benchling/benchlingAPI";
import {
    downloadLegacyFermenterRunTemplate,
    downloadV2FermenterRunTemplate,
    fetchSetupFermenterRunExperimentStatus,
    setupFermenterRunExperiment,
    setupLegacyFermenterRunExperiment,
    ValidFermenterRunSetup
} from "@/services/fermenterRuns/fermenterRunAPI";
import { fetchFermenterRegistryEntries } from "@/services/fermenterRuns/fermenterRunsService";
import { downloadFile } from "@/lib/downloadFileUtil";
import { RegistryEntry } from "@/services/registries/registriesAPI";
import { useToast } from "vue-toastification";
import { useRouter } from "vue-router";
import _ from "lodash";
import { sleepUntil } from "@/lib/sleep";
import { BackgroundProcessEntry, BackgroundProcessStatus } from "@/services/backgroundProcess";
import { ordersAPI, ProductionType, SavedOrder } from "@/services/orders/ordersAPI";
import { EntityLookupResult } from "@/services/commonModels";
import {
    ExperimentGroupDTO,
    experimentGroupsAPI,
} from "@/services/experimentGroups/experimentGroupsAPI";
import { requiredSimpleValueRules } from "@/lib/validationUtils";
import { PlateLayout } from "@/services/commonModels"
import ExperimentMetadataTemplateFields from "@/components/Experiments/ExperimentMetadataTemplateFields.vue";

const toast = useToast();
const router = useRouter();

const fermenters: Ref<RegistryEntry[]> = ref([]);
const fermenterRunProtocols: Ref<BenchlingProtocol[]> = ref([]);
const selectedProjectFolder = ref<BenchlingProjectFolderOption>();
const selectedProtocol = ref<BenchlingProtocol>();
const selectedFermenter = ref<RegistryEntry>();
const experimentGroups = ref<ExperimentGroupDTO[]>();
const selectedExperimentGroupId = ref<string>();
const experimentMetadata = ref<Record<string, any>>({} as unknown as Record<string, any>);
const experimentName = ref("");
const startDate = ref(new Date());
const plateLayout = ref<PlateLayout>("horizontal");
const useOrderedPeptides = ref(true);
const numberOfPeptideBatchesPerPeptide= ref(1);
const numberOfBatchesPerPeptide = ref(1);
const numberOfFermenterBatchesPerPeptideBatch= ref(1);
const groupByFermenterSeed = ref(false);
const peptideBatchesText = ref("");
const peptideBatchesValid = ref(false);
const peptideCodesText = ref("");
const peptideCodesValid = ref(false);
const peptideOrders: Ref<SavedOrder[]> = ref([]);
const openOrders: Ref<SavedOrder[]> = ref([]);
const createBatches = ref(true);

const file = ref<File>();

watch(selectedExperimentGroupId, async () => {
    if (selectedExperimentGroupId.value) {
        await withLoadingScreen(async () => {
            await setExperimentMetadata(selectedExperimentGroupId.value!);
        });
    }
});
async function setExperimentMetadata(experimentGroupId: string) {
    try {
        const response = await experimentGroupsAPI.fetchExperimentGroup(experimentGroupId);
        experimentMetadata.value = response.experimentMetadata || {};
    } catch (error) {
        experimentMetadata.value = {};
    }
}

/*
 * To be removed when we move to using peptide codes only
 */
async function validatePeptideBatchCodes(peptideCodes: string[]): Promise<EntityLookupResult> {
    const result = await ordersAPI.validatePeptideBatchCodes(peptideCodes);
    return {
        valid: result.valid,
        invalidEntityCodes: result.invalidPeptideCodes,
    };
}

/*
 * To be removed when we move to using peptide codes only
 */
function handlePeptideBatchCodesValidated(entityLookupResult: EntityLookupResult) {
    if (entityLookupResult.valid) {
        peptideBatchesText.value = entityLookupResult.validEntityCodes!.join(", ");
        peptideBatchesValid.value = true;
    } else {
        peptideBatchesText.value = "";
        peptideBatchesValid.value = false;
    }
}

/*
 * To be removed when we move to using peptide codes only
 */
function isValidPeptideBatches(): boolean {
    return peptideBatchesValid.value && parsePeptideBatches().length > 0;
}

async function validatePeptideCodes(peptideCodes: string[]): Promise<EntityLookupResult> {
    const result = await ordersAPI.validatePeptides(
        peptideCodes,
        peptideOrders.value.map((o) => o.id)
    );
    return {
        valid: result.valid,
        invalidEntityCodes: result.invalidPeptideCodes,
    };
}

function handlePeptideCodesValidated(entityLookupResult: EntityLookupResult) {
    if (entityLookupResult.valid) {
        peptideCodesText.value = entityLookupResult.validEntityCodes!.join(", ");
        peptideCodesValid.value = true;
    } else {
        peptideCodesText.value = "";
        peptideCodesValid.value = false;
    }
}

function isValidPeptideCodes(): boolean {
    return peptideCodesValid.value && parsePeptideCodes().length > 0;
}

function isValidPeptides(): boolean {
    return (
        (!createBatches.value && isValidPeptideBatches()) ||
        (createBatches.value && isValidPeptideCodes())
    );
}

function isRequiredFieldsSelected(): boolean {
    return (
        !_.isNil(experimentName.value) &&
        !_.isNil(selectedProjectFolder.value) &&
        !_.isNil(selectedProtocol.value) &&
        !_.isNil(selectedFermenter.value) &&
        !_.isNil(plateLayout.value) &&
        !_.isNil(selectedExperimentGroupId) &&
        isValidPeptides()
    );
}

const isTemplateDownloadAvailable = computed(() => {
    return isRequiredFieldsSelected();
});

const isProcessFermenterRunSetupFileAvailable = computed(() => {
    return file.value != null && isRequiredFieldsSelected();
});

const peptideOrderIds = computed(() => {
    return peptideOrders.value.map((o) => o.id);
});

withLoadingScreen(async () => {
    fermenters.value = await fetchFermenterRegistryEntries();
    fermenterRunProtocols.value = await fetchFermenterRunProtocols();
    openOrders.value = await ordersAPI.fetchOpenOrdersByType(ProductionType.MICROBIAL);
    experimentGroups.value = (await experimentGroupsAPI.fetchExperimentGroups()).filter((group) => group.isFermenterRunExperimentGroup);
});

/*
 * To be removed when we move to using peptide codes only
 */
function parsePeptideBatches(): string[] {
    return peptideBatchesText.value
        .split(/\s+|,/)
        .map((s) => s.trim())
        .filter((s) => s.length > 0);
}

function parsePeptideCodes(): string[] {
    return peptideCodesText.value
        .split(/\s+|,/)
        .map((s) => s.trim())
        .filter((s) => s.length > 0);
}

async function downloadV2ExcelFiles() {
    await withLoadingScreen(async () => {
        const protocolCode = selectedProtocol.value!.protocol_id;
        const experimentGroupId = selectedExperimentGroupId.value!;
        const fermenterId = selectedFermenter.value!.id;
        const peptideCodes = parsePeptideCodes();

        const payload = {
            protocolCode,
            experimentGroupId,
            fermenterId,
            useOrderedPeptides: useOrderedPeptides.value,
            numberOfBatchesPerPeptide: numberOfBatchesPerPeptide.value,
            peptideCodes,
            orderIds: peptideOrderIds.value,
            experimentName: experimentName.value!,
            plateLayout: plateLayout.value,
            numberOfPeptideBatchesPerPeptide: numberOfPeptideBatchesPerPeptide.value,
            numberOfFermenterBatchesPerPeptideBatch: numberOfFermenterBatchesPerPeptideBatch.value,
            groupByFermenterSeed: groupByFermenterSeed.value
        };

        const response = await downloadV2FermenterRunTemplate(payload);
        downloadFile(`${experimentName.value!} Setup.xlsx`, response);
    });
}

async function upload() {
    if (createBatches.value) {
        await uploadFermenterBatchSetupFile();
    } else {
        await uploadLegacyFermenterBatchSetupFile();
    }
}

async function uploadFermenterBatchSetupFile() {
    await withLoadingScreen(async () => {
        const protocolCode = selectedProtocol.value!.protocol_id;
        const experimentGroupId = selectedExperimentGroupId.value!;
        const fermenterId = selectedFermenter.value!.id;
        const folderId = selectedProjectFolder.value!.folderId;

        const payload = {
            protocolCode,
            experimentGroupId,
            metadata: experimentMetadata.value,
            fermenterId,
            folderId,
            plateLayout: plateLayout.value,
            startDate: startDate.value!.toISOString(),
            useOrderedPeptides: useOrderedPeptides.value,
            orderIds: peptideOrderIds.value,
            experimentName: experimentName.value!,
            groupByFermenterSeed: groupByFermenterSeed.value,
        };

        const formData = new FormData();
        formData.append("fermenter-run-setup-file", file.value!);
        const experimentJson = JSON.stringify(payload);
        const blob = new Blob([experimentJson], { type: "application/json" });
        formData.append("experiment", blob);

        try {
            const { backgroundProcessId } = await setupFermenterRunExperiment(formData);
            const backgroundProcessEntry = await sleepUntil(
                async (): Promise<BackgroundProcessEntry<ValidFermenterRunSetup> | null> => {
                    const backgroundProcessEntry =
                        await fetchSetupFermenterRunExperimentStatus(backgroundProcessId);
                    if (backgroundProcessEntry.status !== BackgroundProcessStatus.Pending) {
                        return backgroundProcessEntry;
                    } else {
                        return null;
                    }
                },
                5000
            );

            if (backgroundProcessEntry.status === BackgroundProcessStatus.Failed) {
                console.error(backgroundProcessEntry);
                toast.error(backgroundProcessEntry.errors.join(", "), { timeout: false });
            } else {
                const experimentCode = backgroundProcessEntry.result.experimentCode;
                router.push({ path: `/experiment/${experimentCode}` });
            }
        } catch (err) {
            console.log(err);
            const msg = _.get(err, "response.data.error.message") as string | undefined;
            toast.error(msg || JSON.stringify(err), { timeout: false });
        }
    });
}

/**
 * @deprecated remove after we migrate to new Fermenter Run flow
 */
async function downloadLegacyTemplate() {
    await withLoadingScreen(async () => {
        const protocolCode = selectedProtocol.value!.protocol_id;
        const fermenterId = selectedFermenter.value!.id;
        const peptideBatchCodes = parsePeptideBatches();

        const payload = {
            protocolCode,
            fermenterId,
            plateLayout: plateLayout.value,
            useOrderedPeptides: useOrderedPeptides.value,
            numberOfBatchesPerPeptide: numberOfBatchesPerPeptide.value,
            peptideBatchCodes,
            experimentName: experimentName.value!,
        };

        const response = await downloadLegacyFermenterRunTemplate(payload);
        downloadFile(`${experimentName.value!} Setup.xlsx`, response);
    });
}

/**
 * @deprecated remove after we migrate to new Fermenter Run flow
 */
async function uploadLegacyFermenterBatchSetupFile() {
    await withLoadingScreen(async () => {
        const protocolCode = selectedProtocol.value!.protocol_id;
        const experimentGroupId = selectedExperimentGroupId.value!;
        const fermenterId = selectedFermenter.value!.id;
        const folderId = selectedProjectFolder.value!.folderId;

        const payload = {
            protocolCode,
            experimentGroupId,
            fermenterId,
            folderId,
            metadata: experimentMetadata.value,
            plateLayout: plateLayout.value,
            startDate: startDate.value!.toISOString(),
            useOrderedPeptides: useOrderedPeptides.value,
            numberOfBatchesPerPeptide: numberOfBatchesPerPeptide.value,
            experimentName: experimentName.value!,
            file: file.value!,
        };

        try {
            // TODO: use new API for peptides when available
            const { experimentCode } = await setupLegacyFermenterRunExperiment(payload);
            const backgroundProcessEntry = await sleepUntil(
                async (): Promise<BackgroundProcessEntry<unknown> | null> => {
                    const backgroundProcessEntry =
                        await fetchSetupFermenterRunExperimentStatus(experimentCode);
                    if (backgroundProcessEntry.status !== BackgroundProcessStatus.Pending) {
                        return backgroundProcessEntry;
                    } else {
                        return null;
                    }
                },
                5000
            );

            if (backgroundProcessEntry.status === BackgroundProcessStatus.Failed) {
                console.error(backgroundProcessEntry);
                toast.error(backgroundProcessEntry.errors.join(", "), { timeout: false });
            } else {
                router.push({ path: `/experiment/${experimentCode}` });
            }
        } catch (err) {
            console.log(err);
            const msg = _.get(err, "response.data.error.message") as string | undefined;
            toast.error(msg || JSON.stringify(err), { timeout: false });
        }
    });
}
</script>
